import { useMemo, useState, useCallback } from 'react';
import { EnhancedKnowledgeBaseSummaryDto } from '@inference.engine/assets-api-client/web';
import { useDebounce } from '@/shared/hooks';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { usePermissions } from '@/entities/user';
import {
  KnowledgeBaseFilters,
  SortOrder,
} from '@/features/knowledge-bases/model/knowledge-bases.types';
import { logger } from '@/lib/utils/logger';

import type { UseKnowledgeBaseFiltersReturn } from './useKnowledgeBaseFilters.types';

const DEFAULT_FILTERS: KnowledgeBaseFilters = {
  name: '',
  sortOrder: 'asc',
  spaceVisibility: 'all',
} as const;

export const useKnowledgeBaseFilters = (
  knowledgeBases: EnhancedKnowledgeBaseSummaryDto[] = [],
): UseKnowledgeBaseFiltersReturn => {
  const [filters, setFiltersState] = useState<KnowledgeBaseFilters>(DEFAULT_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const {
    canViewOrganizationKnowledgeBases,
    canViewWorkspaceKnowledgeBases,
    canViewFoundationalKnowledgeBases,
  } = usePermissions();

  // Debounce search input to prevent excessive filtering
  const debouncedNameFilter = useDebounce(filters.name, SEARCH_DEBOUNCE_MS);

  // Filter by user permissions - only show KBs the user has access to
  const filterByPermissions = useCallback(
    (items: EnhancedKnowledgeBaseSummaryDto[]): EnhancedKnowledgeBaseSummaryDto[] => {
      return items.filter((item) => {
        if (item.isInternal) return canViewFoundationalKnowledgeBases;
        if (item.workspaceId) return canViewWorkspaceKnowledgeBases;
        return canViewOrganizationKnowledgeBases;
      });
    },
    [
      canViewFoundationalKnowledgeBases,
      canViewOrganizationKnowledgeBases,
      canViewWorkspaceKnowledgeBases,
    ],
  );

  // Memoized filter functions to prevent recreation on every render
  const filterByName = useCallback(
    (items: EnhancedKnowledgeBaseSummaryDto[], name: string): EnhancedKnowledgeBaseSummaryDto[] => {
      if (!name.trim()) return items;

      const searchTerm = name.toLowerCase().trim();
      return items.filter((item) => item.name?.toLowerCase().includes(searchTerm) ?? false);
    },
    [],
  );

  const filterBySpaceVisibility = useCallback(
    (items: EnhancedKnowledgeBaseSummaryDto[]): EnhancedKnowledgeBaseSummaryDto[] => {
      if (filters.spaceVisibility === 'internal') return items.filter((item) => item.isInternal);
      if (filters.spaceVisibility === 'workspace') return items.filter((item) => item.workspaceId);
      if (filters.spaceVisibility === 'organization')
        return items.filter((item) => !item.workspaceId && !item.isInternal);

      return items;
    },
    [filters.spaceVisibility],
  );

  const sortByDate = useCallback(
    (
      items: EnhancedKnowledgeBaseSummaryDto[],
      order: SortOrder,
    ): EnhancedKnowledgeBaseSummaryDto[] => {
      // Create a new array to avoid mutating the original
      return [...items].sort((a, b) => {
        const dateA = new Date(a.updated).getTime();
        const dateB = new Date(b.updated).getTime();

        return order === 'desc' ? dateB - dateA : dateA - dateB;
      });
    },
    [],
  );

  // Main filtering logic with proper error handling
  const filteredKnowledgeBases = useMemo(() => {
    try {
      let result = knowledgeBases;

      // Apply filters in sequence
      result = filterByPermissions(result);
      result = filterBySpaceVisibility(result);
      result = filterByName(result, debouncedNameFilter);
      result = sortByDate(result, filters.sortOrder);

      return result;
    } catch (err) {
      // eslint-disable-next-line i18next/no-literal-string -- dead field: the returned `error` is never destructured by a consumer
      const errorMessage = err instanceof Error ? err.message : 'Filtering failed';
      setError(errorMessage);
      logger.error('Knowledge base filtering error:', err);
      return knowledgeBases; // Return original data on error
    }
  }, [
    knowledgeBases,
    debouncedNameFilter,
    filters.sortOrder,
    filterByPermissions,
    filterBySpaceVisibility,
    filterByName,
    sortByDate,
  ]);

  // Computed values
  const totalCount = knowledgeBases.length;
  const filteredCount = filteredKnowledgeBases.length;

  // Action handlers
  const setFilters = useCallback((newFilters: Partial<KnowledgeBaseFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const updateFilter = useCallback(
    <K extends keyof KnowledgeBaseFilters>(key: K, value: KnowledgeBaseFilters[K]) => {
      setFiltersState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setError(null);
  }, []);

  return {
    // Data
    filteredKnowledgeBases,
    filters,
    debouncedSearch: debouncedNameFilter,

    // Actions
    setFilters,
    updateFilter,
    resetFilters,

    // State
    error,

    // Computed
    totalCount,
    filteredCount,
  };
};
